import { createApi } from "@reduxjs/toolkit/query/react"
import { RootState } from "appRedux/store"
import { baseQueryWithMiddlewares, getPublicAwareUrl } from "appRedux/baseQuery"
import { updateFollowRequests, updateUserProfile, updateUserWishListsWithFirstFourItems } from "appRedux/slices/userSlice"
import { updateCurrentItemIndex, updateCurrentItemInfo, updateWishListInfo } from "appRedux/slices/wishListSlice"
import { updateFollowersTab, updateFollowingsTab } from "appRedux/slices/profileTabsSlice"
import { updateUserSearchResult } from "appRedux/slices/exploreSlice"
import { updateViewedUserWishItems, updateViewedUserWishListsWithFirstFourItems } from "appRedux/slices/viewedUserSlice"
import { updateViewedUserProfile } from "appRedux/slices/viewedUserSlice"
import {
  handleOnAcceptFollowRequestQueryStarted,
  handleOnCreateUnfollowRequestQueryStarted,
  handleOnRemoveFollowerQueryStarted,
  handleOnGetCloseFriendsQueryStarted,
  handleOnUpdateCloseFriendQueryStarted
} from "appRedux/helpers/userApiHelpers"

const BASE_PATH = "/user"

export const userProfileApi = createApi({
  reducerPath: "userProfileApi",
  baseQuery: baseQueryWithMiddlewares(BASE_PATH),
  endpoints: (builder) => ({
    getWishListsWithFirstFourItems: builder.query<GetWishListsResponseDto, string>({
      query: (handle) => getPublicAwareUrl(BASE_PATH, `/${handle}/wishlist`),
      keepUnusedDataFor: 5,
      onQueryStarted: async (handle, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateViewedUserWishListsWithFirstFourItems(data))

          const state = getState() as RootState
          const userProfile = state.user.profile
          if (userProfile?.handle === handle) {
            dispatch(updateUserWishListsWithFirstFourItems(data))
          }
        } catch (error) {
          console.error("Error getting wishlists by handle (with first four items):", error)
        }
      }
    }),
    getUserProfile: builder.query<Profile, void>({
      query: () => "/profile",
      keepUnusedDataFor: 0,
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateUserProfile(data))
        } catch (error) {
          console.error("Error getting userProfile:", error)
        }
      }
    }),
    updateUserProfile: builder.mutation({
      query: (body) => ({
        url: "/profile",
        method: "PATCH",
        body: body,
        formData: true
      }),
      onQueryStarted: async (formDataBody, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          if (!userProfile) return
          let objectBody = Object.fromEntries(formDataBody.entries()) as UserProfileUpdateRequestPayload
          // @ts-ignore
          dispatch(updateUserProfile({ ...userProfile, ...objectBody }))
        } catch (error) {
          console.error("Error updating userProfile:", error)
        }
      }
    }),
    searchUser: builder.query<SearchUserResponseDto, void>({
      query: (keyword) => `/search?searchQuery=${keyword}`,
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateUserSearchResult(data))
        } catch (error) {
          console.error("Error searching user with username:", error)
        }
      }
    }),
    getUserProfileByHandle: builder.query<Profile, string>({
      query: (handle) => getPublicAwareUrl(BASE_PATH, `/profile/${handle}`),
      keepUnusedDataFor: 0,
      onQueryStarted: async (handle, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateViewedUserProfile(data))
        } catch (error) {
          console.error("Error getting userProfile by handle:", error)
        }
      }
    }),
    getUserWishItems: builder.query<GetWishItemsResponseDto, string>({
      query: (handle) => getPublicAwareUrl(BASE_PATH, `/${handle}/item`),
      keepUnusedDataFor: 5,
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateViewedUserWishItems(data))
        } catch (error) {
          console.error("Error getting user wish items:", error)
        }
      }
    }),
    getUserWishItem: builder.query<SingleWishItem, { handle: string; id: number }>({
      query: ({ handle, id }) => getPublicAwareUrl(BASE_PATH, `/${handle}/item/${id}`),
      keepUnusedDataFor: 5,
      onQueryStarted: async ({ handle, id }, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateCurrentItemInfo(data))
        } catch (error) {
          console.error(`Error getting user wish item with handle: ${handle} and id: ${id}:`, error)
        }
      }
    }),
    getUserWishList: builder.query<WishList, { handle: string; id: number }>({
      query: ({ handle, id }) => getPublicAwareUrl(BASE_PATH, `/${handle}/wishlist/${id}`),
      keepUnusedDataFor: 5,
      onQueryStarted: async ({ handle, id }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateWishListInfo(data))
        } catch (error) {
          dispatch(updateWishListInfo(null))
          dispatch(updateCurrentItemInfo(null))
          dispatch(updateCurrentItemIndex(-1))
          console.error(`Error getting user wishlist with handle: ${handle} and id: ${id}: `, error)
        }
      }
    }),
    createFollowRequest: builder.mutation<void, number>({
      query: (followeeId) => ({
        url: `/${followeeId}/follow`,
        method: "POST"
      })
    }),
    createUnfollowRequest: builder.mutation<void, number>({
      query: (followeeId) => ({
        url: `/${followeeId}/unfollow`,
        method: "POST"
      }),
      onQueryStarted: handleOnCreateUnfollowRequestQueryStarted
    }),
    createCancelFollowRequest: builder.mutation<void, number>({
      query: (followeeId) => ({
        url: `/${followeeId}/cancel-follow`,
        method: "POST"
      })
    }),
    acceptFollowRequest: builder.mutation<void, number>({
      query: (followerId) => ({
        url: `/${followerId}/follow-request/accept`,
        method: "POST"
      }),
      onQueryStarted: handleOnAcceptFollowRequestQueryStarted
    }),
    rejectFollowRequest: builder.mutation<void, number>({
      query: (followerId) => ({
        url: `/${followerId}/follow-request/reject`,
        method: "POST"
      })
    }),
    getFollowRequests: builder.query<FollowRequestsResponseDto, void>({
      query: () => "/follow-request",
      onQueryStarted: async (arg, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateFollowRequests(data.followRequests))
        } catch (error) {
          console.error("Error getting follow requests:", error)
        }
      }
    }),
    getFollowers: builder.query<GetFollowersResponseDto, number>({
      query: (id) => `/${id}/followers`,
      keepUnusedDataFor: 0,
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateFollowersTab(data.users))
        } catch (error) {
          console.error("Error getting followers for profile id:", error)
        }
      }
    }),
    getFollowings: builder.query<GetFollowingsResponseDto, number>({
      query: (id) => `/${id}/followings`,
      keepUnusedDataFor: 0,
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        try {
          const { data } = await queryFulfilled
          dispatch(updateFollowingsTab(data.users))
        } catch (error) {
          console.error("Error getting followings for profile id:", error)
        }
      }
    }),
    removeFollower: builder.mutation<void, number>({
      query: (id) => ({
        url: `/${id}/remove`,
        method: "POST"
      }),
      onQueryStarted: handleOnRemoveFollowerQueryStarted
    }),
    updateAvatar: builder.mutation({
      query: (body) => ({
        url: "/avatar",
        method: "PATCH",
        body: body,
        formData: true
      }),
      onQueryStarted: async (formDataBody, { dispatch, queryFulfilled, getState }) => {
        try {
          await queryFulfilled
          const state = getState() as RootState
          const userProfile = state.user.profile
          if (!userProfile) return
          let objectBody = Object.fromEntries(formDataBody.entries()) as UserProfileUpdateRequestPayload
          // @ts-ignore
          dispatch(updateUserProfile({ ...userProfile, avatar: objectBody.avatar.size === 0 ? null : objectBody.avatar }))
        } catch (error) {
          console.error("Error updating userProfile:", error)
        }
      }
    }),
    getCloseFriends: builder.query<GetCloseFriendsResponseDto, void>({
      query: () => "/close-friends",
      keepUnusedDataFor: 0,
      onQueryStarted: handleOnGetCloseFriendsQueryStarted
    }),
    updateCloseFriends: builder.mutation<void, UpdateCloseFriendsRequestPayload>({
      query: (body) => ({
        url: "/close-friends",
        method: "POST",
        body
      })
    }),
    updateCloseFriend: builder.mutation<void, number>({
      query: (id) => ({
        url: `/close-friend/${id}`,
        method: "POST"
      }),
      onQueryStarted: handleOnUpdateCloseFriendQueryStarted
    })
  })
})

export const {
  useLazyGetWishListsWithFirstFourItemsQuery,
  useLazyGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useLazySearchUserQuery,
  useLazyGetUserProfileByHandleQuery,
  useLazyGetUserWishItemsQuery,
  useLazyGetUserWishItemQuery,
  useLazyGetUserWishListQuery,
  useCreateFollowRequestMutation,
  useCreateUnfollowRequestMutation,
  useCreateCancelFollowRequestMutation,
  useAcceptFollowRequestMutation,
  useRejectFollowRequestMutation,
  useGetFollowRequestsQuery,
  useLazyGetFollowersQuery,
  useLazyGetFollowingsQuery,
  useRemoveFollowerMutation,
  useUpdateAvatarMutation,
  useLazyGetCloseFriendsQuery,
  useUpdateCloseFriendsMutation,
  useUpdateCloseFriendMutation
} = userProfileApi
